/**
 * Validator Registry v3.0
 *
 * Centralized validation logic for all question types.
 * Separated from questionTypeRegistry for clean architecture.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/validatorRegistry.ts in R25.
 * Split into sub-modules (R1-S1 god-file split) in 2026-05.
 *
 * Sub-modules:
 *   validators/shared.ts              — Types, constants, normalizeNumeric
 *   validators/mathValidators.ts      — 13 math/numeric validators
 *   validators/chartValidators.ts     — 4 chart selection validators
 *   validators/languageValidators.ts  — 7 language/grammar validators
 *   validators/interactiveValidators.ts — 6 interactive/drag-drop validators
 */

import type { QuestionType, Question } from '../types'
import type { ValidationResult } from '../../shared/answerContract'

/**
 * @deprecated Import directly from `./validators/shared` instead.
 * These re-exports exist for backward compatibility and will be removed.
 */
export type { ValidatorFn } from './validators/shared'
/** @deprecated Import from `./validators/shared` instead. */
export { NUMERIC_TOLERANCE, normalizeNumeric } from './validators/shared'

// ── Import all validators ────────────────────────────────────

import {
  validateMultipleChoice,
  validateColumnArithmetic,
  validateLongDivision,
  validateClock,
  validateMoney,
  validateFillIn,
  validateNumberSequence,
  validateRatioTable,
  validateEstimationSlider,
  validateGridShade,
  validateStepSolver,
  validateMultiplicationGrid,
  validateThermometerSlider,
  validateSquareRootBuilder,
  validatePlaceValueBuild,
  validateBarModelPart,
  validateStrategySolver,
  validateFractionStripBuilder,
} from './validators/mathValidators'

import {
  validateBarChart,
  validateLineChart,
  validatePieChart,
} from './validators/chartValidators'

import {
  validateTextHighlight,
  validateTrueFalseSeries,
  validateErrorSpotting,
  validateSentenceParser,
  validateComparisonJudge,
  validateVerbConjugationFlow,
  validateSentenceErrorJudge,
} from './validators/languageValidators'

import {
  validateOrdering,
  validateMatching,
  validateDragDropFill,
  validateTableReading,
  validateScheduleReading,
  validateCategorize,
  validateFractionEquivalenceMatch,
} from './validators/interactiveValidators'

import {
  validateMapLocateRegion,
  validateMapLocatePoint,
  validateMapLocateLine,
  validateMapPlaceCities,
} from './validators/mapValidators'

// ── Registry ─────────────────────────────────────────────────

export const VALIDATOR_REGISTRY: Record<
  QuestionType,
  (userAnswer: string | number, question: Question) => ValidationResult
> = {
  multiple_choice: validateMultipleChoice,
  cijferend_rekenen: validateColumnArithmetic,
  cijferend_delen: validateLongDivision,
  clock: validateClock,
  clock_set: validateClock,
  money: validateMoney,
  fill_in: validateFillIn,
  ordering: validateOrdering,
  matching: validateMatching,
  drag_drop_fill: validateDragDropFill,
  table_reading: validateTableReading,
  number_sequence: validateNumberSequence,
  ratio_table: validateRatioTable,
  text_highlight: validateTextHighlight,
  bar_chart: validateBarChart,
  line_chart: validateLineChart,
  pie_chart: validatePieChart,
  schedule_reading: validateScheduleReading,
  multiplication_grid: validateMultiplicationGrid,
  true_false_series: validateTrueFalseSeries,
  estimation_slider: validateEstimationSlider,
  categorize: validateCategorize,
  error_spotting: validateErrorSpotting,
  grid_shade: validateGridShade,
  step_solver: validateStepSolver,
  sentence_parser: validateSentenceParser,
  comparison_judge: validateComparisonJudge,
  verb_conjugation_flow: validateVerbConjugationFlow,
  thermometer_slider: validateThermometerSlider,
  sentence_error_judge: validateSentenceErrorJudge,
  map_locate_region: validateMapLocateRegion,
  map_locate_point: validateMapLocatePoint,
  map_locate_line: validateMapLocateLine,
  map_place_cities: validateMapPlaceCities,
  square_root_builder: validateSquareRootBuilder,
  place_value_build: validatePlaceValueBuild,
  bar_model_part: validateBarModelPart,
  strategy_solver: validateStrategySolver,
  fraction_equivalence_match: validateFractionEquivalenceMatch,
  fraction_strip_builder: validateFractionStripBuilder,
}

// ── Public API ───────────────────────────────────────────────

/**
 * Get the validator function for a question type.
 * Throws if the type is obsolete or unsupported.
 */
export function getValidator(
  questionType: string,
): (userAnswer: string | number, question: Question) => ValidationResult {
  if (questionType === 'geld_samenstellen' || questionType === 'klok_aflezen') {
    throw new Error(
      `Obsolete questionType "${questionType}" detected. Migrate to canonical "money" or "clock".`,
    )
  }
  const validator = VALIDATOR_REGISTRY[questionType as QuestionType]
  if (!validator) {
    throw new Error(
      `Unsupported questionType "${questionType}" in validatorRegistry.`,
    )
  }
  return validator
}

/**
 * Validate an answer using the appropriate validator.
 * This is the primary API for answer validation.
 */
export function validateAnswer(
  questionType: string,
  userAnswer: string | number,
  question: Question,
): ValidationResult {
  const validator = getValidator(questionType)
  return validator(userAnswer, question)
}
